<template>
  <div>
    <Spinner v-if="!isInitialized" />
    <div class="image-container" v-if="isInitialized">
      <v-img
        class="the-image"
        :src="url"
        :lazy-src="urlLowRes"
        @click="onClickImage"
        max-width="100%"
        contain
        position="top center"
      >
      </v-img>

      <div class="settings">
        <div class="top-row">
          <div>
            <v-btn
              elevation="2"
              color="primary"
              class="mr-2"
              @click="save"
              :disabled="!hasUnsavedUpdates"
              :loading="isSaving"
              >Spara</v-btn
            >
            <CommonMenu :items="[{ key: 'remove', value: 'Radera bild' }]" @remove="onRemove" />
          </div>
        </div>
        <TemplateImageStandard v-bind="image" @update="update" class="my-3" />
      </div>
    </div>
    <Message ref="message" />
    <Confirm ref="confirm" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import TemplateImageStandard from '@/components/templates/ImageStandard'
import Message from '@/components/shared/Message'
import Spinner from '@/components/shared/Spinner'
import Confirm from '@/components/shared/Confirm'
import { getColor } from '@/assets/data/colors'
import CommonMenu from '@/components/shared/CommonMenu'

export default {
  name: 'SingleImage',
  props: {
    id: { required: true },
  },
  components: {
    Spinner,
    TemplateImageStandard,
    Message,
    Confirm,
    CommonMenu,
  },
  data() {
    return {
      isInitialized: false,
      isSaving: false,
      updates: {},
      updatesContent: {},
    }
  },
  computed: {
    ...mapGetters('image', { getImageById: 'getById' }),
    hasUnsavedUpdates() {
      return !!Object.keys({ ...this.updates, ...this.updatesContent }).length
    },
    imageId() {
      return +this.id
    },
    image() {
      return { ...this.getImageById(this.imageId), ...this.updates }
    },
    url() {
      return `${process.env.VUE_APP_IMAGE_BACKEND}/870/${this.image.id}.jpg`
    },
    urlLowRes() {
      return `${process.env.VUE_APP_IMAGE_BACKEND}/170/${this.image.id}.jpg`
    },
    srcSet() {
      return `${process.env.VUE_APP_IMAGE_BACKEND}/1170/${this.image.id}.jpg 870w`
    },
    publishOptions() {
      return [
        { id: 0, value: 'online', title: 'Online', color: getColor('online') },
        { id: 1, value: 'offline', title: 'Offline', color: getColor('offline') },
        { id: 2, value: 'authorize', title: 'Authorize', color: getColor('authorize') },
      ]
    },
    publishIndex() {
      return this.publishOptions.findIndex((o) => o.value === this.image.image_publish)
    },
  },
  methods: {
    ...mapActions('image', {
      getSingleImage: 'read',
      updateImage: 'update',
      deleteImage: 'delete',
    }),
    onClickImage() {
      this.$router.push({ ...this.$route, hash: `#image:${this.id}` })
    },
    async onRemove() {
      const shouldRemove = await this.$refs.confirm.open({
        title: 'Ta bort bilden',
        message: 'Är du säker på att du vill ta bort bilden?',
        saveButtonText: 'Ja',
        cancelButtonText: 'Avbryt',
      })
      if (!shouldRemove) return

      await this.deleteImage({ ...this.getImageById(this.imageId) })
      this.$router.push({ name: 'ImagesList' })
    },
    async saveImmediately(payload) {
      this.isSaving = true

      await this.updateImage({ ...this.image, ...payload })

      this.isSaving = false
    },
    async save() {
      try {
        if (!this.hasUnsavedUpdates) return

        this.isSaving = true

        await this.updateImage({ ...this.image, ...this.updates })

        this.updates = {}

        this.$refs.message.open('Bildinfo sparad')
      } catch (err) {
        throw new Error(err)
      } finally {
        this.isSaving = false
      }
    },
    update(payload) {
      this.updates = { ...this.updates, ...payload }
    },
    setPublish(index) {
      const { value } = this.publishOptions[index]
      this.saveImmediately({ image_publish: value })
    },
  },
  async created() {
    await this.getSingleImage({ id: +this.id })
    this.isInitialized = true
  },
  mounted() {
    this._keyListener = function (e) {
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault() // present "Save Page" from getting triggered.
        this.save()
      }
    }
    document.addEventListener('keydown', this._keyListener.bind(this))
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this._keyListener)
  },
  async beforeRouteLeave(to, from, next) {
    if (!this.hasUnsavedUpdates) {
      next()
      return
    }
    const shouldSave = await this.$refs.confirm.open({
      title: 'Osparade ändringar',
      message: 'Vill du spara innen du går vidare?',
      saveButtonText: 'Spara',
      cancelButtonText: 'Nej',
    })
    if (shouldSave) await this.save()

    next()
  },
}
</script>

<style lang="less" scoped>
.image-container {
  display: grid;
  grid-template-columns: 1fr 500px;
  gap: 15px;
  .the-image {
    display: grid;
    cursor: pointer;
  }
  .top-row {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 15px;
  }
}
.active-chip {
  color: red;
}
</style>
